import { apiClient } from '@api/client';
import { AuthorizationBoundary } from '@components/AuthorizationBoundary';
import { ErrorPage404 } from '@pages/ErrorPage';
import { useMutation } from '@tanstack/react-query';
import React, { ReactElement, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface AsanaOAuthInnerProps {
  code: string;
  state: string;
  handle: string;
  loader: ReactElement;
}

function AsanaOAuthInner({
  code,
  state,
  loader,
  handle
}: AsanaOAuthInnerProps) {
  const { mutate, isError } = useMutation({
    mutationFn: async () => {
      await apiClient.asana.asanaControllerConnect({
        code,
        state,
        handle
      });
    },
    onSuccess: () => {
      if (window.opener) {
        window.opener.postMessage('success', 'https://app.asana.com');
        window.close();
      } else {
        window.location.href = 'https://app.asana.com/';
      }
    },
    onError: () => {
      if (window.opener) {
        window.opener.postMessage('error', 'https://app.asana.com');
        window.close();
      }
    }
  });
  useEffect(() => {
    mutate();
  }, [mutate]);
  if (isError) {
    return <ErrorPage404 />;
  }
  return loader;
}

interface AsanaOAuthProps {
  loader: ReactElement;
}

export default function AsanaOAuth({ loader }: AsanaOAuthProps) {
  const [searchParams] = useSearchParams();
  if (searchParams.get('error')) {
    // https://developers.asana.com/docs/app-component-approval-checklist#3-handle-the-deny-use-case
    // If the user denies the app, Asana will redirect to the redirect_uri with an error query parameter
    if (window.opener) {
      window.opener.postMessage('error', 'https://app.asana.com');
      window.close();
    }
    return <ErrorPage404 />;
  }
  if (searchParams.get('code') || searchParams.get('state')) {
    // Auth0 uses 'code' and 'state' query parameters for its own auth flow
    // We need to rename code and state returned by Asana to avoid conflicts
    searchParams.set('_code', searchParams.get('code') ?? '');
    searchParams.set('_state', searchParams.get('state') ?? '');
    searchParams.delete('code');
    searchParams.delete('state');
    window.location.search = searchParams.toString();
    return loader;
  }
  const code = searchParams.get('_code') ?? '';
  const state = searchParams.get('_state') ?? '';
  const handle = sessionStorage.getItem('asana-oauth-handle') ?? '';
  return (
    <AuthorizationBoundary
      loader={loader}
      element={
        <AsanaOAuthInner
          loader={loader}
          code={code}
          state={state}
          handle={handle}
        />
      }
      renderError={() => <ErrorPage404 />}
    />
  );
}
